import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import { Card, Button } from "@/components/ui";

function RowTable({ rows }) {
  if (!rows?.length) return <p className="p-4 text-xs text-muted-foreground">—</p>;
  const cols = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            {cols.map((c) => <th key={c} className="whitespace-nowrap px-3 py-2 text-start capitalize">{c.replace(/_/g, " ")}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/40 last:border-0">
              {cols.map((c) => <td key={c} className="whitespace-nowrap px-3 py-2">{String(r[c])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Schedules() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();
  const [tab, setTab] = useState("doors");

  const { data, loading, error } = useDeliverable(
    () => api.editorSchedules({ model: wm.model, floors: wm.floors || 1, slab_system: wm.slab_system, building_kind: wm.kind, design_code: wm.design_code }),
    [wm?.title, wm?.design_code],
    !!wm?.model
  );

  const tabs = [
    ["doors", isAr ? "الأبواب" : "Doors"],
    ["windows", isAr ? "النوافذ" : "Windows"],
    ["columns", isAr ? "الأعمدة" : "Columns"],
    ["rebar", isAr ? "التسليح" : "Rebar"],
  ];
  const sec = data?.[tab];

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-5xl px-4 py-6">
          <PageHeader eyebrow={isAr ? "الجداول" : "Schedules"} title={isAr ? "جداول الأبواب والنوافذ والأعمدة والتسليح" : "Door / window / column / rebar schedules"} />
          <DeliverableState loading={loading} error={error} isAr={isAr}>
            <div className="mb-4 flex flex-wrap gap-2">
              {tabs.map(([k, l]) => (
                <Button key={k} size="sm" variant={tab === k ? "primary" : "outline"} onClick={() => setTab(k)} data-testid={`sched-${k}`}>{l}</Button>
              ))}
            </div>
            {sec && (
              <Card className="p-0 overflow-hidden">
                <div className="border-b border-border bg-white/5 px-4 py-2 text-sm font-black">{isAr ? sec.title_ar : sec.title_en}</div>
                {tab === "rebar" ? (
                  <div className="space-y-4 p-4">
                    <RowTable rows={sec.summary_by_dia} />
                    <div className="flex gap-6 text-sm font-bold">
                      <span>{isAr ? "الوزن الكلي" : "Total"}: {sec.total_weight_kg} kg ({sec.total_weight_ton} t)</span>
                      <span className="text-muted-foreground">{isAr ? "الهدر" : "Wastage"}: {sec.wastage_pct}%</span>
                    </div>
                  </div>
                ) : (
                  <RowTable rows={sec.rows} />
                )}
              </Card>
            )}
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
